/**
 * HeroControl — ONE compact "Hero ▾" workbench control for dossier heroes.
 * All capability lives inside the drawer: regenerate, adjust & regenerate,
 * upload, candidate preview/select/discard, history restore. The current
 * public hero is never altered until the owner explicitly selects.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, Image as ImageIcon, Loader2, RotateCcw, Upload, X } from "lucide-react";
import { toast } from "sonner";
import {
  heroCandidates, heroCandidateGenerate, heroCandidateSelect,
  heroCandidateDiscard, heroCandidateUpload, heroHistory, heroHistoryRestore,
} from "../../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const img = (u) => (u && u.startsWith("/") ? `${API}${u}` : u);

export const HeroControl = ({ slug, opToken, userToken }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [hist, setHist] = useState([]);
  const [scene, setScene] = useState("");
  const [showAdjust, setShowAdjust] = useState(false);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [c, h] = await Promise.all([
        heroCandidates(opToken, slug, userToken),
        heroHistory(opToken, slug, userToken),
      ]);
      setData(c);
      setHist(h.history || []);
      if (c?.job?.state === "running") {
        clearTimeout(pollRef.current);
        pollRef.current = setTimeout(load, 6000);
      }
    } catch { /* drawer shows empty state */ }
  }, [opToken, slug, userToken]);

  useEffect(() => { if (open) load(); return () => clearTimeout(pollRef.current); }, [open, load]);

  const generate = async (adjusted) => {
    setBusy("gen");
    try {
      await heroCandidateGenerate(opToken, slug, adjusted ? { scene } : {}, userToken);
      toast.success("Generating candidate — current hero untouched");
      setShowAdjust(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setBusy(""); }
  };
  const select = async (file) => {
    setBusy(file);
    try {
      await heroCandidateSelect(opToken, slug, file, userToken);
      toast.success("Hero replaced — previous hero saved to history");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Select failed"); }
    finally { setBusy(""); }
  };
  const discard = async (file) => {
    try { await heroCandidateDiscard(opToken, slug, file, userToken); load(); } catch { /* noop */ }
  };
  const restore = async (file) => {
    setBusy(file);
    try {
      await heroHistoryRestore(opToken, slug, file, userToken);
      toast.success("Previous hero restored (original asset)");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
    finally { setBusy(""); }
  };
  const upload = async (f) => {
    if (!f) return;
    setBusy("up");
    try {
      await heroCandidateUpload(opToken, slug, f, userToken);
      toast.success("Uploaded as candidate — select to use it");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(""); }
  };

  if (!slug) return null;
  const generating = data?.job?.state === "running";

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="hero-control-btn"
        className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#E4DFD3] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
        <ImageIcon size={11} /> Hero <ChevronDown size={10} />
      </button>

      {open && (
        <div className="fixed inset-0 z-[200] bg-black/80 flex justify-end" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md h-full overflow-y-auto bg-[#12110e] border-l border-[#2D2A26] p-4"
               onClick={(e) => e.stopPropagation()} data-testid="hero-drawer">
            <div className="flex items-center gap-2 mb-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">// HERO — {slug}</span>
              <button onClick={() => setOpen(false)} className="ml-auto text-[#757065] w-9 h-9 inline-flex items-center justify-center" data-testid="hero-drawer-close"><X size={15} /></button>
            </div>

            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">Current hero</div>
            {data?.current ? (
              <img src={img(data.current)} alt="current hero" className="w-full aspect-[21/9] object-cover border border-[#3A3630] mb-3" data-testid="hero-current-img" />
            ) : (
              <div className="w-full aspect-[21/9] border border-[#3A3630] flex items-center justify-center font-mono text-[9px] uppercase tracking-[0.2em] text-[#5A554C] mb-3">No hero yet</div>
            )}

            <div className="flex flex-wrap gap-2 mb-4">
              <button onClick={() => generate(false)} disabled={generating || busy === "gen"} data-testid="hero-regenerate"
                className="border border-[#9FA8D4]/40 text-[#9FA8D4] disabled:opacity-40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]">
                {generating ? <Loader2 size={11} className="inline animate-spin mr-1" /> : null} Regenerate
              </button>
              <button onClick={() => setShowAdjust((v) => !v)} disabled={generating} data-testid="hero-adjust-toggle"
                className="border border-[#454038] text-[#A39E93] disabled:opacity-40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]">
                Adjust & regenerate
              </button>
              <button onClick={() => fileRef.current?.click()} disabled={busy === "up"} data-testid="hero-upload"
                className="border border-[#454038] text-[#A39E93] disabled:opacity-40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]">
                <Upload size={10} className="inline mr-1" /> Upload
              </button>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                     onChange={(e) => upload(e.target.files?.[0])} />
            </div>
            {showAdjust && (
              <div className="mb-4">
                <textarea value={scene} onChange={(e) => setScene(e.target.value)} rows={3}
                  placeholder="Describe the scene adjustments…" data-testid="hero-scene-input"
                  className="w-full bg-black border border-[#3A3630] text-[#E4DFD3] text-xs p-2 font-mono" />
                <button onClick={() => generate(true)} disabled={generating || !scene.trim()} data-testid="hero-adjust-generate"
                  className="mt-2 border border-[#9FA8D4]/40 text-[#9FA8D4] disabled:opacity-40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em]">
                  Generate with adjustments
                </button>
              </div>
            )}
            {generating && (
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#E0A526] mb-3">
                <Loader2 size={10} className="inline animate-spin mr-1" /> Candidate generating — the current hero stays live
              </p>
            )}
            {data?.job?.state === "failed" && (
              <p className="font-mono text-[9px] text-[#D92D20] mb-3">Last generation failed: {data.job.error}</p>
            )}

            {(data?.candidates || []).length > 0 && (
              <>
                <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">Candidates</div>
                {(data.candidates || []).map((c) => (
                  <div key={c.file} className="mb-3" data-testid={`hero-candidate-${c.file}`}>
                    <img src={img(c.url)} alt="candidate" className="w-full aspect-[21/9] object-cover border border-[#3A3630]" />
                    <div className="flex gap-2 mt-1">
                      <button onClick={() => select(c.file)} disabled={!!busy} data-testid={`hero-select-${c.file}`}
                        className="border border-[#5DD4B3]/50 text-[#5DD4B3] px-2 py-1 font-mono text-[9px] uppercase tracking-[0.2em]">Use this</button>
                      <button onClick={() => discard(c.file)}
                        className="border border-[#454038] text-[#757065] px-2 py-1 font-mono text-[9px] uppercase tracking-[0.2em]">Discard</button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {hist.length > 0 && (
              <>
                <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mt-2 mb-1">Previous heroes</div>
                {hist.map((h) => (
                  <div key={h.file} className="mb-3" data-testid={`hero-history-${h.file}`}>
                    <img src={img(h.url)} alt="previous hero" className="w-full aspect-[21/9] object-cover border border-[#3A3630] opacity-80" />
                    <div className="flex items-center gap-2 mt-1">
                      <button onClick={() => restore(h.file)} disabled={!!busy}
                        className="border border-[#CBB892]/50 text-[#CBB892] px-2 py-1 font-mono text-[9px] uppercase tracking-[0.2em]">
                        <RotateCcw size={9} className="inline mr-1" /> Restore
                      </button>
                      <span className="font-mono text-[8px] text-[#5A554C]">{(h.at || "").slice(0, 16).replace("T", " ")} · {h.kind}</span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default HeroControl;
